/**
 * Rashi chart rendering, in both traditional Indian forms.
 *
 * North Indian ("diamond"): houses are FIXED in position, signs rotate — house
 * 1 is always the top-centre diamond, and each cell is labelled with the sign
 * number that falls there.
 *
 * South Indian ("square"): signs are FIXED in position — Pisces top-left, then
 * clockwise — and the Lagna is marked wherever it falls.
 *
 * DESIGN_SPEC §6.4 specifies a single circular 300px chart. That form is
 * Western, not Vedic, and cannot show the house/sign relationship either
 * tradition depends on; the spec needs amending to match what is drawn here.
 */

const NS = 'http://www.w3.org/2000/svg';

export interface ChartPlacement {
  /** Short label, e.g. "Su", "Mo". */
  abbr: string;
  /** 0-11, sidereal sign index. */
  signIndex: number;
  retrograde?: boolean;
  /** Rendered greyed when the birth time can't pin it down. */
  uncertain?: boolean;
}

function svgEl<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number> = {},
): SVGElementTagNameMap[K] {
  const node = document.createElementNS(NS, tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, String(v));
  return node;
}

function frame(title: string): SVGSVGElement {
  const svg = svgEl('svg', {
    viewBox: '0 0 300 300',
    class: 'chart-svg',
    role: 'img',
    'aria-label': title,
  });
  return svg;
}

/** Draws the grahas sitting in one cell, stacked and centred. */
function drawCell(
  svg: SVGSVGElement,
  cx: number,
  cy: number,
  label: string,
  bodies: ChartPlacement[],
): void {
  const tag = svgEl('text', { x: cx, y: cy - 6 * Math.max(0, bodies.length - 1) - 8, class: 'chart-svg__cell-label' });
  tag.textContent = label;
  svg.appendChild(tag);

  bodies.forEach((body, i) => {
    const text = svgEl('text', {
      x: cx,
      y: cy - 6 * Math.max(0, bodies.length - 1) + 6 + i * 13,
      class: `chart-svg__body${body.uncertain ? ' is-uncertain' : ''}`,
    });
    text.textContent = body.retrograde ? `${body.abbr}ᴿ` : body.abbr;
    svg.appendChild(text);
  });
}

/**
 * North Indian diamond. `ascendantSign` decides which sign number sits in
 * house 1; the rest follow in order.
 */
export function northIndianChart(
  placements: ChartPlacement[],
  ascendantSign: number,
): SVGSVGElement {
  const svg = frame('North Indian rashi chart');

  // Outer square, both diagonals, and the inner diamond through the midpoints.
  svg.appendChild(svgEl('rect', { x: 2, y: 2, width: 296, height: 296, class: 'chart-svg__line' }));
  svg.appendChild(svgEl('line', { x1: 2, y1: 2, x2: 298, y2: 298, class: 'chart-svg__line' }));
  svg.appendChild(svgEl('line', { x1: 298, y1: 2, x2: 2, y2: 298, class: 'chart-svg__line' }));
  svg.appendChild(
    svgEl('polygon', { points: '150,2 298,150 150,298 2,150', class: 'chart-svg__line' }),
  );

  /* Label positions for houses 1-12, in the traditional order: house 1 at
     top centre, then anticlockwise. */
  const centres: [number, number][] = [
    [150, 62], [76, 40], [40, 76], [62, 150], [40, 224], [76, 260],
    [150, 238], [224, 260], [260, 224], [238, 150], [260, 76], [224, 40],
  ];

  centres.forEach(([cx, cy], house) => {
    const signIndex = (ascendantSign + house) % 12;
    const bodies = placements.filter((p) => p.signIndex === signIndex);
    drawCell(svg, cx, cy, String(signIndex + 1), bodies);
  });

  return svg;
}

/**
 * South Indian square. Signs are fixed: Pisces top-left, running clockwise.
 * The Lagna cell is outlined.
 */
export function southIndianChart(
  placements: ChartPlacement[],
  ascendantSign: number,
): SVGSVGElement {
  const svg = frame('South Indian rashi chart');

  /* Grid column/row for each sign index 0-11 (Aries..Pisces) in the fixed
     South Indian arrangement. */
  const cells: Record<number, [number, number]> = {
    0: [1, 0], 1: [2, 0], 2: [3, 0], 3: [3, 1],
    4: [3, 2], 5: [3, 3], 6: [2, 3], 7: [1, 3],
    8: [0, 3], 9: [0, 2], 10: [0, 1], 11: [0, 0],
  };

  const size = 74;
  const pad = 2;

  for (let sign = 0; sign < 12; sign++) {
    const [col, row] = cells[sign]!;
    const x = pad + col * size;
    const y = pad + row * size;

    svg.appendChild(
      svgEl('rect', {
        x, y, width: size, height: size,
        class: `chart-svg__line${sign === ascendantSign ? ' is-lagna' : ''}`,
      }),
    );

    const bodies = placements.filter((p) => p.signIndex === sign);
    drawCell(svg, x + size / 2, y + size / 2, sign === ascendantSign ? 'Lagna' : String(sign + 1), bodies);
  }

  return svg;
}
